<template>
  <div :class="localeClass">
    <div class="viewer__header">
      <div class="viewer__headerLeft">
        <img src="/img/reality_icon.png" alt="" />
        <p class="viewer_headerText">{{ $t('appDownload1') }}</p>
      </div>
      <div
        class="viewer__headerRight"
        @click="redirectToCustomURLSchemeForAppOpen"
        href=""
      >
        {{ $t('openApp') }}
      </div>
    </div>

    <modal
      v-if="!isUserWebview"
      :showModal="showModal"
      :showCloseButton="true"
      @close="closeModal"
    >
      <p class="modal_text">{{ $t('appDownload2') }}</p>

      <PrimaryButton
        class="modal_button"
        @click="redirectToCustomURLSchemeForAppOpen"
      >
        {{ $t('openApp') }}
      </PrimaryButton>
    </modal>

    <div class="viewer__videoContainer" v-show="shouldShowVideo">
      <video
        id="video"
        class="viewer__video video-js vjs-big-play-centered"
        preload="auto"
        data-setup="{}"
        playsinline
        autoplay
        controls
      ></video>
      <media-info
        v-if="media && isLoggedIn"
        :media="media"
        :isFollowing="isFollowing"
        @followStateChanged="onFollowStateChanged"
        @iconClick="openModal"
        class="viewer__mediaInfoArea"
      />
      <div class="viewer__comment">
        <comment-area
          class="viewer__commentArea"
          :comments="this.comments"
          @clickName="openModal"
        />
        <comment-form
          class="viewer__commentForm"
          :mediaId="mediaId"
          v-if="isLoggedIn"
          @iconClick="openModal"
        />
      </div>
    </div>

    <div class="viewer__lidPictureContainer" v-show="shouldShowLidPicture">
      <div class="viewer__lidPicture">
        <img v-bind:src="iconUrl" class="viewer__lidPicture_thumbnail" />
        <div class="viewer__lidPicture_nickname">{{ nickname }}</div>
        <div class="viewer__lidPicture_title">{{ title }}</div>
      </div>
    </div>

    <div v-show="shouldShowSchedule" class="viewer__scheduleContainer">
      <div class="viewer__schedule">
        <strong>{{ $t('broadcastIsNotStarted') }}</strong>
        <div>{{ $t('broadcastSchedule') }}: {{ this.startTimeText }}</div>
        <p>{{ $t('broadcastNotification') }}</p>
      </div>
    </div>

    <not-found class="viewer__notfound" v-show="shouldShow404"></not-found>
  </div>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import { api } from '@/api/appGatewayServerApi'
import { ApiError } from '@/api/error'
import videojs from 'video.js'
import NotFound from '@/components/Error/NotFound.vue'
import CommentArea from '@/components/Viewer/CommentArea.vue'
import CommentForm from '@/components/Viewer/CommentForm.vue'
import MediaInfo from '@/components/Viewer/MediaInfo.vue'
import Modal from '@/components/Modal.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import { redirectToTrackerURLIfNeeded } from '@/utils/adjust_redirector'
import { Comment, CommentPayload } from '@/models/Comment'
import { Media, BroadcastingMethods, MediaStates } from '@/models/Media'
import { showGenericNetworkError, sendApiWarning } from '@/utils/handle_error'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import { getTokenFromCookie } from '@/utils/token'
import { redirectToCustomURLSchemeForAppOpen } from '@/utils/adjust_redirector'

@Component({
  components: {
    NotFound,
    MediaInfo,
    CommentArea,
    CommentForm,
    Modal,
    PrimaryButton,
  },
})
export default class Viewer extends Mixins(LanguageQueryHandler) {
  media: Media | null = null
  state: number = 30
  player: any = null
  enableVideo: boolean = true
  title: string = ''
  nickname: string = ''
  iconUrl: string = ''
  startTime: number = 0
  socket: WebSocket | null = null
  comments: Comment[] = []
  recentCommentId: number = 0
  isFollowing: boolean = false
  showModal: boolean = false

  get mediaId() {
    const pathPattern = /\/viewer\/(\d+)/
    return pathPattern.test(location.pathname) ? Number(location.pathname.replace('/viewer/', '')) : 0
  }

  get isUserWebview(): boolean {
    return getTokenFromCookie() !== undefined
  }

  created() {
    redirectToTrackerURLIfNeeded()
  }

  async mounted() {
    try {
      const media = await this.fetchMedia(this.mediaId)
      this.$set(this, 'media', media)

      this.state = media.state
      if (this.shouldShow404) {
        return
      }

      this.startTime = Date.parse(media.startTime)
      if (media.broadcastingMethod === BroadcastingMethods.RTMP) {
        // RTMP
        this.setupPlayer(media.streamingServer.viewEndpoint)
        this.setupCommentServer(media.chatServer.endpointIpaddr)
      } else {
        // ClientRendering
        this.setupLidPicture(
          media.title,
          media.mediaStreamers[0].nickname,
          media.mediaStreamers[0].iconUrl
        )
      }

      if (this.isLoggedIn) {
        const followState = await this.fetchFollowState(
          media.ownerStreamer.vliveId
        )
        this.isFollowing = followState.isFollowing
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // セッション切れなのでトークンを消す
        this.$store.dispatch('session/logoutExpired', e.errorCode)
      } else {
        sendApiWarning(e)
        showGenericNetworkError()
      }
    }
  }

  get isLoggedIn() {
    return this.$store.getters['session/isLoggedIn']
  }

  get startTimeText() {
    if (this.startTime === 0) {
      return ''
    }

    var d = new Date(this.startTime)
    var month = d.getMonth() + 1
    var day = d.getDate()
    var hour = ('0' + d.getHours()).slice(-2)
    var min = ('0' + d.getMinutes()).slice(-2)
    var sec = ('0' + d.getSeconds()).slice(-2)

    return month + '/' + day + ' ' + hour + ':' + min + ':' + sec
  }

  async fetchMedia(mediaId: number) {
    const response = await api().media.get({ mediaId })
    return response.media
  }

  async fetchFollowState(vliveId: string) {
    const response = await api().streamer.follow.exists({ vliveId })
    return response.followState
  }

  onFollowStateChanged(value: boolean) {
    this.isFollowing = value
  }

  openModal() {
    this.showModal = true
  }

  closeModal() {
    this.showModal = false
  }

  get shouldShowVideo() {
    return this.state === MediaStates.Live && this.enableVideo
  }

  get shouldShowLidPicture() {
    return this.state === MediaStates.Live && !this.enableVideo
  }

  get shouldShow404() {
    return this.state !== MediaStates.Ready && this.state !== MediaStates.Live
  }

  get shouldShowSchedule() {
    return this.state === MediaStates.Ready
  }

  redirectToCustomURLSchemeForAppOpen = redirectToCustomURLSchemeForAppOpen

  setupPlayer(endpoint: string) {
    this.player = videojs('video')
    this.player.src({
      type: 'application/x-mpegURL',
      src: endpoint,
    })
    // 自動再生に失敗するとrejectされるのでエラーは無視
    this.player.play().catch(() => {})
  }

  getGiftComment(comment: CommentPayload): string {
    if (comment.gift_detail) {
      return comment.gift_detail.comment
    }
    return comment.content.split(',')[3] //MEMO:将来contentにgiftCommentを入れなくなったら消す
  }

  setupCommentServer(endpoint: string) {
    this.socket = new WebSocket(endpoint)
    this.socket.addEventListener('message', (event) => {
      var json: CommentPayload = JSON.parse(event.data)

      let comment: Comment = {
        name: json.nickname,
        content_type: json.content_type,
        id: this.recentCommentId,
        icon_url: json.icon_url,
      }

      if (this.shouldSkipContent(json.content_type)) {
        return
      }

      this.recentCommentId += 1

      if (json.content_type === 0 || json.content_type === 1) {
        comment.class = 'comment'
        comment.content = json.content
        comment.shouldDisplayName = true
      } else if (json.content_type === 3) {
        comment.class = 'gift'
        comment.content = this.getGiftComment(json)
        comment.shouldDisplayName = true
      } else if (json.content_type === 6) {
        comment.class = 'soft'
        comment.content = this.getGiftComment(json)
        comment.shouldDisplayName = true
      } else if (json.content_type === 10) {
        comment.class = 'avatarGift'
        comment.content = this.getGiftComment(json)
        comment.shouldDisplayName = true
      } else if (json.content_type === 100) {
        comment.class = 'system'
        comment.content = this.$t('commentBroadcastEnded')
        comment.shouldDisplayName = false
      } else {
        comment.class = 'system'
        comment.content = json.content
      }

      const showCommentsLimit = 100
      if (this.comments.length > showCommentsLimit) {
        this.comments.shift()
      }
      this.comments.push(comment)
    })
  }

  private shouldSkipContent(contentType: number): boolean {
    const contentTypes = [101, 102, 103]
    return contentTypes.includes(contentType)
  }

  setupLidPicture(title: string, nickname: string, iconUrl: string) {
    this.enableVideo = false
    this.title = title
    this.nickname = nickname
    this.iconUrl = iconUrl
  }

  get localeClass() {
    return 'viewer__container -' + this.$i18n.locale
  }
}
</script>

<style scoped>
.viewer__container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.viewer__videoContainer {
  width: 100%;
  height: 93%;
}

.viewer__video {
  width: 100%;
  height: 100%;
}

.viewer__lidPictureContainer {
  width: 100%;
  height: 93%;

  background-color: #f2f2f2;
}

.viewer__lidPicture {
  width: 100%;

  position: absolute;
  top: 36%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.viewer__lidPicture_thumbnail {
  width: 100px;
  border-radius: 50px;
  margin-bottom: 16px;
}

.viewer__lidPicture_nickname {
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #262626;
  margin: 0px 16px 4px;
}

.viewer__lidPicture_title {
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  color: #262626;
  margin: 0px 16px 0px;
}

.viewer__notfound {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
}

.viewer__scheduleContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.viewer__schedule {
  text-align: center;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 150px;
}

.viewer__header {
  width: 100%;
  height: 52px;
  padding: 14px 16px;
  top: 0px;
  box-shadow: 0 -8px 34px 0 rgba(0, 0, 0, 0.1);
  background-color: #000000;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.viewer__headerLeft {
  height: 24px;
  line-height: 24px;
  display: flex;
  align-items: center;
}

.viewer__headerLeft img {
  height: 24px;
}

.viewer_headerText {
  font-size: 12px;
  line-height: 16px;
  color: #ffffff;
  font-weight: 600;
  margin-left: 8px;
}

.viewer__headerRight {
  flex-direction: column;
  background-color: #188dff;
  height: 24px;
  width: 88px;
  border-radius: 12px;
  padding: 4px 8px;

  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 12px;
  line-height: 16px;
  font-weight: bold;
}

.viewer__comment {
  width: 100%;
  position: fixed;
  bottom: 16px;
}

.viewer__commentArea {
  height: 200px;
}

.viewer__commentForm {
  margin: 8px 0 0 0;
  padding: 0 16px;
}

.viewer__mediaInfoArea {
  width: 100%;
  position: fixed;
  top: 50px;
  height: 200px;
  padding: 16px;
}

.modal_text {
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  color: #262626;
  margin-top: 32px;
}

.modal_button {
  width: 100%;
  margin: 16px 0 0 0;
}
</style>

<!-- i18n patch styles-->
<style scoped>
@import 'https://vjs.zencdn.net/6.6.3/video-js.css';

.-ru .viewer_headerText,
.-fr .viewer_headerText,
.-de .viewer_headerText,
.-es .viewer_headerText,
.-ms .viewer_headerText,
.-id .viewer_headerText,
.-pt-BR .viewer_headerText,
.-en .viewer_headerText {
  font-size: 9px;
  line-height: 10px;
  padding-right: 5px;
}

.-ru .viewer__headerRight,
.-fr .viewer__headerRight,
.-de .viewer__headerRight,
.-es .viewer__headerRight,
.-ms .viewer__headerRight,
.-id .viewer__headerRight,
.-pt-BR .viewer__headerRight,
.-en .viewer__headerRight {
  font-size: 9px;
  line-height: 9px;
  width: 110px;
  padding: 2px 8px;
  text-align: center;
}
</style>
